import api from './api';

/**
 * Conversation / messaging API service (Centralized Messaging — Phase 3).
 *
 * Every call must carry the active workspace context so the backend can scope
 * the query. `ctx` is `{ workspaceType: 'personal' | 'organization', organizationId? }`.
 * Methods unwrap the `{ success, data }` envelope and return `data`.
 */

function workspaceParams(ctx = {}) {
  const params = {};
  if (ctx.workspaceType) params.workspaceType = ctx.workspaceType;
  if (ctx.organizationId) params.organizationId = ctx.organizationId;
  return params;
}

const conversationService = {
  /**
   * List conversations for the active workspace (cursor paginated).
   * @returns {Promise<{ items: Array, nextCursor: string|null }>}
   */
  listConversations: async (ctx, { cursor, limit } = {}) => {
    const params = { ...workspaceParams(ctx) };
    if (cursor) params.cursor = cursor;
    if (limit) params.limit = limit;
    const res = await api.get('/conversations', { params });
    return {
      items: res.data.data || [],
      nextCursor: res.data.nextCursor || null,
    };
  },

  /** Workspace members available to start a direct conversation with. */
  listWorkspaceMembers: async (ctx) => {
    const res = await api.get('/conversations/members', {
      params: workspaceParams(ctx),
    });
    return res.data.data || [];
  },

  /**
   * Search messages across the caller's conversations (workspace-scoped).
   * @returns {Promise<{ items: Array, nextCursor: string|null }>}
   */
  searchMessages: async (ctx, query, { cursor, limit } = {}) => {
    const params = { ...workspaceParams(ctx), q: query };
    if (cursor) params.cursor = cursor;
    if (limit) params.limit = limit;
    const res = await api.get('/conversations/search', { params });
    return {
      items: res.data.data || [],
      nextCursor: res.data.nextCursor || null,
    };
  },

  /** Open (or create) a direct conversation with another workspace member. */
  openDirect: async (ctx, targetUserId) => {
    const res = await api.post(
      '/conversations/direct',
      { targetUserId },
      { params: workspaceParams(ctx) }
    );
    return res.data.data;
  },

  /** Open (or create) the shared conversation for a project. */
  openProject: async (ctx, projectId) => {
    const res = await api.post(
      `/conversations/project/${projectId}`,
      {},
      { params: workspaceParams(ctx) }
    );
    return res.data.data;
  },

  /** Fetch a single conversation view (metadata + participants). */
  getConversation: async (ctx, conversationId) => {
    const res = await api.get(`/conversations/${conversationId}`, {
      params: workspaceParams(ctx),
    });
    return res.data.data;
  },

  /** Create a custom group conversation (Phase 5.3). */
  createGroup: async (ctx, { name, memberIds }) => {
    const res = await api.post(
      '/conversations/group',
      { name, memberIds },
      { params: workspaceParams(ctx) }
    );
    return res.data.data;
  },

  /** Rename a group conversation (admin only). */
  renameGroup: async (ctx, conversationId, name) => {
    const res = await api.patch(
      `/conversations/${conversationId}/group`,
      { name },
      { params: workspaceParams(ctx) }
    );
    return res.data.data;
  },

  /** Update the display profile for project/team/group chats. */
  updateProfile: async (ctx, conversationId, profile) => {
    const res = await api.patch(
      `/conversations/${conversationId}/profile`,
      profile,
      { params: workspaceParams(ctx) }
    );
    return res.data.data;
  },

  /** Add members to a group conversation (admin only). */
  addGroupMembers: async (ctx, conversationId, memberIds) => {
    const res = await api.post(
      `/conversations/${conversationId}/members`,
      { memberIds },
      { params: workspaceParams(ctx) }
    );
    return res.data.data;
  },

  /** Remove a member from a group conversation (admin only). */
  removeGroupMember: async (ctx, conversationId, memberId) => {
    const res = await api.delete(
      `/conversations/${conversationId}/members/${memberId}`,
      { params: workspaceParams(ctx) }
    );
    return res.data.data;
  },

  /** Change a group member's role (admin only). */
  updateGroupMemberRole: async (ctx, conversationId, memberId, role) => {
    const res = await api.patch(
      `/conversations/${conversationId}/members/${memberId}/role`,
      { role },
      { params: workspaceParams(ctx) }
    );
    return res.data.data;
  },

  /** Leave a group conversation. */
  leaveGroup: async (ctx, conversationId) => {
    const res = await api.post(
      `/conversations/${conversationId}/leave`,
      {},
      { params: workspaceParams(ctx) }
    );
    return res.data.data;
  },

  /** Update the caller's per-conversation flags (mute/pin/archive/star). */
  updateFlags: async (ctx, conversationId, flags) => {
    const res = await api.patch(
      `/conversations/${conversationId}/flags`,
      flags,
      { params: workspaceParams(ctx) }
    );
    return res.data.data;
  },

  /**
   * Fetch messages for a conversation (cursor paginated, newest-first window).
   * @returns {Promise<{ items: Array, nextCursor: string|null }>}
   */
  getMessages: async (ctx, conversationId, { cursor, limit } = {}) => {
    const params = { ...workspaceParams(ctx) };
    if (cursor) params.cursor = cursor;
    if (limit) params.limit = limit;
    const res = await api.get(`/conversations/${conversationId}/messages`, {
      params,
    });
    return {
      items: res.data.data || [],
      nextCursor: res.data.nextCursor || null,
    };
  },

  /** Send a message to a conversation. */
  sendMessage: async (ctx, conversationId, payload) => {
    const res = await api.post(
      `/conversations/${conversationId}/messages`,
      payload,
      { params: workspaceParams(ctx) }
    );
    return res.data.data;
  },

  /** Edit a message's text content. */
  editMessage: async (ctx, conversationId, messageId, content) => {
    const res = await api.patch(
      `/conversations/${conversationId}/messages/${messageId}`,
      { content },
      { params: workspaceParams(ctx) }
    );
    return res.data.data;
  },

  /** Soft-delete a message. */
  deleteMessage: async (ctx, conversationId, messageId) => {
    const res = await api.delete(
      `/conversations/${conversationId}/messages/${messageId}`,
      { params: workspaceParams(ctx) }
    );
    return res.data.data;
  },

  /** Toggle an emoji reaction on a message. */
  toggleReaction: async (ctx, conversationId, messageId, emoji) => {
    const res = await api.post(
      `/conversations/${conversationId}/messages/${messageId}/reactions`,
      { emoji },
      { params: workspaceParams(ctx) }
    );
    return res.data.data;
  },

  /** Mark a conversation as read up to now. */
  markRead: async (ctx, conversationId) => {
    const res = await api.post(
      `/conversations/${conversationId}/read`,
      {},
      { params: workspaceParams(ctx) }
    );
    return res.data.data;
  },
};

export default conversationService;
